import { AttemptsService } from "@/client"
import { Stack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import { AttemptsList } from "@/components/Attempts/AttemptsList"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { QuizFilterControls } from "@/components/Quizzes/QuizFilterControls"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute("/_layout/attempts-history")({
  component: MyAttemptsPage,
})

function MyAttemptsPage() {
  const { attemptsLabel } = useStudentLabels()
  const [search, setSearch] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const { data, isLoading } = useQuery({
    queryKey: ["my-attempts"],
    queryFn: () => AttemptsService.getMyQuizAttempts({ skip: 0, limit: 100 }),
  })

  const breadcrumbs = useRouteBreadcrumbs("attempts-history.index")
  const description = "Review your quiz attempts and revisit completed quizzes."

  if (isLoading) {
    return (
      <PageShell
        title={attemptsLabel}
        description={description}
        breadcrumbs={breadcrumbs}
      >
        <PageSkeleton variant="table" rows={5} />
      </PageShell>
    )
  }

  const attempts = data?.data ?? []
  const availableSubjects = Array.from(
    new Map(
      attempts.flatMap((attempt) =>
        (attempt.subjects ?? []).map((subject) => [subject.id, subject]),
      ),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name))
  const normalizedSearch = search.trim().toLowerCase()
  const filteredAttempts = attempts.filter(
    (attempt) =>
      (!normalizedSearch ||
        attempt.quiz_name.toLowerCase().includes(normalizedSearch)) &&
      (!selectedSubjects.length ||
        selectedSubjects.some((subject) =>
          (attempt.subjects ?? []).some((item) => item.id === subject.id),
        )) &&
      (!selectedTags.length ||
        selectedTags.every((tag) => (attempt.tags ?? []).includes(tag))),
  )

  return (
    <PageShell
      title={attemptsLabel}
      description={description}
      breadcrumbs={breadcrumbs}
    >
      <Stack gap={6}>
        <QuizFilterControls
          search={search}
          onSearchChange={setSearch}
          selectedSubjects={selectedSubjects}
          onSubjectsChange={setSelectedSubjects}
          availableSubjects={availableSubjects}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
        />
        <AttemptsList attempts={filteredAttempts} />
      </Stack>
    </PageShell>
  )
}
